import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Card } from '@startuptools/ui/Card';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { GenericErrorText } from '@startuptools/ui/Error';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import React from 'react';
import { CreateWarrantProgramForm } from './CreateWarrantProgram';
import { GqlSnrBond, useCreateWpFromSnrBondMutation } from '../../../graphql/react-operations';
import { WpTableRow } from '../common/OptionPrograms';
import { DialogTitle } from '@startuptools/ui/Dialog';
import { Modal } from '@startuptools/ui/joy/Modal';
import { ModalClose } from '@startuptools/ui/joy/ModalClose';
import { ModalDialog } from '@startuptools/ui/joy/ModalDialog';
import { wpEditLink } from '../../../components/Links';
import { useNavigate } from '../../../components/Navigate';

export const SnrBondsTable = ({
  snrBonds,
}: {
  snrBonds: Pick<
    GqlSnrBond,
    'id' | 'filingNumber' | 'filingYear' | 'numberOfOptions' | 'decisionDate' | 'shareSubStartDate' | 'shareSubEndDate'
  >[];
}) => {
  const companyId = useCompanyId();

  const [createFromBond, { error, loading }] = useCreateWpFromSnrBondMutation();
  const { navigate } = useNavigate();
  const { i18n } = useLingui();
  const [showCreate, setShowCreate] = React.useState<{ id: string } | undefined>(undefined);

  const isDuplicateName = error?.message === 'description already in use';

  return (
    <>
      <Card sx={{ overflow: 'scroll' }}>
        <Typography level="h2">
          <Trans id="Previous warrant programs" />
        </Typography>
        <Alert>
          <Trans id="We've found these programs registered with Bolagsverket. Click on a row below to register them on Bolago." />
        </Alert>
        <Table hoverRow sx={{ minWidth: '1000px' }}>
          <thead>
            <WpTableRow
              name={<Trans id="Name" />}
              maxNumberOfOptions={<Trans id="Maximum Number of Options" />}
              decisionDate={<Trans id="Decision" />}
              firstDateToSubscribeForShares={<Trans id="Start subscription window" />}
              lastDateToSubscribeForShares={<Trans id="End subscription window" />}
              actions=""
              header
            />
          </thead>
          <tbody>
            {snrBonds.map(bond => (
              <WpTableRow
                onClick={() => {
                  setShowCreate({ id: bond.id });
                }}
                key={bond.id}
                name={bond.filingNumber + '/' + bond.filingYear}
                maxNumberOfOptions={
                  <NumberFormat maximumFractionDigits={0} locale={i18n.locale}>
                    {bond.numberOfOptions}
                  </NumberFormat>
                }
                decisionDate={<DateFormat locale={i18n.locale}>{bond.decisionDate}</DateFormat>}
                firstDateToSubscribeForShares={<DateFormat locale={i18n.locale}>{bond.shareSubStartDate}</DateFormat>}
                lastDateToSubscribeForShares={<DateFormat locale={i18n.locale}>{bond.shareSubEndDate}</DateFormat>}
                actions={''}
              />
            ))}
          </tbody>
        </Table>
      </Card>

      {showCreate && (
        <Modal open onClose={() => setShowCreate(undefined)}>
          <ModalDialog>
            <ModalClose />

            <React.Suspense>
              <DialogTitle>{<Trans id="Complementary information" />}</DialogTitle>
              <CreateWarrantProgramForm
                onClose={() => {
                  setShowCreate(undefined);
                }}
                loading={loading}
                error={
                  isDuplicateName ? (
                    <Trans id="A warrant program with this description already exists" />
                  ) : error ? (
                    <GenericErrorText />
                  ) : undefined
                }
                type="bond"
                onSave={async ({ description }) => {
                  const res = await createFromBond({
                    variables: {
                      companyId,
                      input: {
                        description,
                        snrBondId: showCreate.id,
                      },
                    },
                  });
                  if (res?.data?.createWpFromSnrBond?.id) {
                    const wp = res.data.createWpFromSnrBond;
                    return navigate(wpEditLink(wp));
                  }
                }}
              />
            </React.Suspense>
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};
